import React, { useState } from 'react';
import { ChevronRight, Check, Building2, Calendar, DollarSign, Package, Search } from 'lucide-react';
import { cn } from '../../lib/utils';

/**
 * FlowStep Component
 *
 * Renders a single step in a conversational flow with selectable options.
 * Supports multiple option types: OEM selection, program cards, segments, revenue ranges.
 *
 * Props:
 * - stepData: Object containing step configuration
 *   - step_name: 'select_oem', 'select_program', 'select_segment', 'select_revenue', 'confirm'
 *   - step_title: Display title for the step
 *   - step_description: Description text
 *   - options_type: 'oem_selection', 'program_cards', 'segment_buttons', 'revenue_buttons'
 *   - options: Array of selectable options
 *   - allow_custom: Boolean - allow custom text input
 *   - custom_placeholder: Placeholder for custom input
 * - onSelect: (selectedValue, optionData) => void - Called when user selects an option
 * - onCustomInput: (text) => void - Called when user submits custom text
 * - isLoading: Boolean - Show loading state
 */
const FlowStep = ({
  stepData,
  onSelect,
  onCustomInput,
  isLoading = false,
  className
}) => {
  const [customText, setCustomText] = useState('');
  const [selectedOption, setSelectedOption] = useState(null);

  if (!stepData) return null;

  const {
    step_name,
    step_title,
    step_description,
    options_type,
    options,
    oem_options,
    allow_custom,
    custom_placeholder
  } = stepData;

  const handleOptionClick = (option, value) => {
    setSelectedOption(value);
    onSelect?.(value, option);
  };

  const handleCustomSubmit = (e) => {
    e.preventDefault();
    if (customText.trim()) {
      onCustomInput?.(customText.trim());
      setCustomText('');
    }
  };

  // OEM Selection - Grid of OEM buttons
  const renderOEMSelection = () => (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-2">
        {(oem_options || []).map((oem, idx) => (
          <button
            key={idx}
            onClick={() => handleOptionClick({ name: oem }, oem)}
            disabled={isLoading}
            className={cn(
              'flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg border transition-all',
              selectedOption === oem
                ? 'bg-[#07689f] text-white border-[#07689f]'
                : 'bg-white text-gray-700 border-gray-200 hover:border-[#07689f] hover:bg-[#e8f4fa]',
              isLoading && 'opacity-50 cursor-not-allowed'
            )}
          >
            <Building2 className="h-4 w-4" />
            {oem}
          </button>
        ))}
      </div>
    </div>
  );

  // Program Cards - Cards showing program details
  const renderProgramCards = () => (
    <div className="space-y-2 max-h-[300px] overflow-y-auto pr-1">
      {(options || []).map((program, idx) => (
        <button
          key={program.id || idx}
          onClick={() => handleOptionClick(program, program.name)}
          disabled={isLoading || program.has_opportunity}
          className={cn(
            'w-full text-left p-3 rounded-lg border transition-all',
            selectedOption === program.name
              ? 'bg-[#e8f4fa] border-[#07689f]'
              : program.has_opportunity
              ? 'bg-gray-50 border-gray-200 opacity-60 cursor-not-allowed'
              : 'bg-white border-gray-200 hover:border-[#07689f] hover:bg-[#f8fcfe]',
            isLoading && 'opacity-50 cursor-not-allowed'
          )}
        >
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <span className="font-medium text-gray-900">{program.name}</span>
                {program.has_opportunity && (
                  <span className="text-xs px-2 py-0.5 bg-gray-200 text-gray-600 rounded">
                    Has opportunity
                  </span>
                )}
              </div>
              <div className="flex flex-wrap gap-2 mt-1 text-xs text-gray-500">
                {program.platform && (
                  <span className="flex items-center gap-1">
                    <Package className="h-3 w-3" />
                    {program.platform}
                  </span>
                )}
                {program.sop_date && (
                  <span className="flex items-center gap-1">
                    <Calendar className="h-3 w-3" />
                    SOP: {program.sop_date}
                  </span>
                )}
              </div>
            </div>
            {selectedOption === program.name && (
              <Check className="h-5 w-5 text-[#07689f]" />
            )}
          </div>
        </button>
      ))}
      {(!options || options.length === 0) && (
        <div className="text-center py-4 text-gray-500 text-sm">
          No programs available. You can create a custom opportunity below.
        </div>
      )}
    </div>
  );

  // Segment Buttons - Simple button grid
  const renderSegmentButtons = () => {
    const segments = options || stepData.segments || [
      { id: 'connectors', name: 'Connectors' },
      { id: 'sensors', name: 'Sensors' },
      { id: 'modules', name: 'Modules' },
      { id: 'harnesses', name: 'Harnesses' },
      { id: 'switches', name: 'Switches' },
      { id: 'other', name: 'Other' }
    ];

    return (
      <div className="flex flex-wrap gap-2">
        {segments.map((segment, idx) => (
          <button
            key={segment.id || idx}
            onClick={() => handleOptionClick(segment, segment.name)}
            disabled={isLoading}
            className={cn(
              'px-4 py-2 text-sm font-medium rounded-full border transition-all',
              selectedOption === segment.name
                ? 'bg-[#07689f] text-white border-[#07689f]'
                : 'bg-white text-gray-700 border-gray-200 hover:border-[#07689f] hover:bg-[#e8f4fa]',
              isLoading && 'opacity-50 cursor-not-allowed'
            )}
          >
            {segment.name}
          </button>
        ))}
      </div>
    );
  };

  // Revenue Buttons - Revenue range options
  const renderRevenueButtons = () => {
    const ranges = [
      { id: 'under_1m', label: '< $1M', icon: DollarSign },
      { id: '1m_5m', label: '$1M - $5M', icon: DollarSign },
      { id: '5m_10m', label: '$5M - $10M', icon: DollarSign },
      { id: '10m_plus', label: '$10M+', icon: DollarSign },
      { id: 'not_sure', label: 'Not sure yet', icon: null }
    ];

    return (
      <div className="grid grid-cols-2 gap-2">
        {ranges.map((range) => (
          <button
            key={range.id}
            onClick={() => handleOptionClick(range, range.id)}
            disabled={isLoading}
            className={cn(
              'flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium rounded-lg border transition-all',
              selectedOption === range.id
                ? 'bg-[#07689f] text-white border-[#07689f]'
                : 'bg-white text-gray-700 border-gray-200 hover:border-[#07689f] hover:bg-[#e8f4fa]',
              isLoading && 'opacity-50 cursor-not-allowed'
            )}
          >
            {range.icon && <range.icon className="h-4 w-4" />}
            {range.label}
          </button>
        ))}
      </div>
    );
  };

  // Render options based on type
  const renderOptions = () => {
    switch (options_type) {
      case 'oem_selection':
        return renderOEMSelection();
      case 'program_cards':
        return renderProgramCards();
      case 'segment_buttons':
        return renderSegmentButtons();
      case 'revenue_buttons':
        return renderRevenueButtons();
      default:
        // Default to segment buttons if we have segments data
        if (stepData.segments) {
          return renderSegmentButtons();
        }
        return null;
    }
  };

  return (
    <div className={cn('space-y-4', className)}>
      {/* Step Header */}
      {step_title && (
        <div className="border-b border-gray-100 pb-2">
          <h4 className="text-sm font-semibold text-gray-900">{step_title}</h4>
          {step_description && (
            <p className="text-xs text-gray-500 mt-0.5">{step_description}</p>
          )}
        </div>
      )}

      {/* Options */}
      {renderOptions()}

      {/* Custom Input */}
      {allow_custom && (
        <form onSubmit={handleCustomSubmit} className="mt-3">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={customText}
                onChange={(e) => setCustomText(e.target.value)}
                placeholder={custom_placeholder || 'Or type a custom name...'}
                disabled={isLoading}
                className={cn(
                  'w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg',
                  'focus:outline-none focus:ring-2 focus:ring-[#07689f] focus:border-transparent',
                  'placeholder:text-gray-400',
                  isLoading && 'opacity-50 cursor-not-allowed'
                )}
              />
            </div>
            <button
              type="submit"
              disabled={!customText.trim() || isLoading}
              className={cn(
                'px-4 py-2 text-sm font-medium rounded-lg transition-all',
                'bg-[#07689f] text-white hover:bg-[#055a7d]',
                (!customText.trim() || isLoading) && 'opacity-50 cursor-not-allowed'
              )}
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default FlowStep;
